import { useEffect, useState } from "react";
import LocationButton from "../components/LocationButton";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { ShieldCheck, UserPlus, ArrowLeft, User, Truck, Wrench, Building2, Navigation, Upload, X, BadgeCheck } from "lucide-react";
import { api, photoUrl, uploadFiles, formatApiError } from "../api";
import { useAuth } from "../context/AuthContext";
import { WORK_DAYS, slugify, trUpper } from "../lib/labels";

const inputCls = "w-full px-4 py-3 rounded-xl bg-slate-950/70 border border-white/10 text-slate-100 text-sm focus:outline-none focus:border-cyan-400/60 transition-colors";
const labelCls = "block text-xs font-semibold text-slate-400 uppercase tracking-wider mb-1.5";
const FAMILY_ICON = { cekici: Truck, servis: Wrench, diger: Building2 };

const EMPTY_BIZ = {
  name: "", email: "", password: "", company_name: "", tax_number: "", phone: "",
  city: "", district: "", address: "", services: [], regions: [], working_days: [],
  working_hours: "", is_247: false, available: true, whatsapp: "", website: "",
  description: "", logo: "", photos: [], tax_document: "", lat: 0, lng: 0,
};

export default function Register() {
  const [catalog, setCatalog] = useState([]);
  const [kind, setKind] = useState(null); // null | "user" | "isletme"
  const [typeKey, setTypeKey] = useState(null);
  const [f, setF] = useState({ ...EMPTY_BIZ });
  const [regionInput, setRegionInput] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState("");
  const { setUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from;

  useEffect(() => {
    api.get("/isletme-turleri").then((r) => setCatalog(r.data)).catch(() => setCatalog([]));
  }, []);

  const sel = catalog.find((c) => c.key === typeKey);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const toggleArr = (k, v) => setF((p) => ({ ...p, [k]: p[k].includes(v) ? p[k].filter((x) => x !== v) : [...p[k], v] }));
  const addRegion = () => {
    const r = regionInput.trim();
    if (r && !f.regions.includes(r)) set("regions", [...f.regions, r]);
    setRegionInput("");
  };
  const doUpload = async (files, key, single) => {
    setUploading(key);
    try {
      const paths = await uploadFiles(files);
      setF((p) => ({ ...p, [key]: single ? paths[0] : [...p[key], ...paths].slice(0, 6) }));
    } catch (err) { toast_error(err); }
    setUploading("");
  };
  const toast_error = (err) => setError(formatApiError(err));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (kind !== "isletme" && !f.data_consent) { setError("Devam etmek için araç verileri paylaşım onayını işaretleyin"); return; }
    setLoading(true);
    try {
      const payload = kind === "isletme"
        ? { ...f, account_type: "isletme", business_type: typeKey }
        : { name: f.name, email: f.email, password: f.password, account_type: "user",
            membership_type: kind === "kurumsal" ? "kurumsal" : "bireysel",
            company_name: kind === "kurumsal" ? f.company_name : "", city: f.city, district: f.district,
            data_consent: !!f.data_consent };
      const { data } = await api.post("/auth/register", payload);
      setUser(data);
      navigate(data.role === "cekici" ? "/cekici-panel" : data.role === "servis" ? "/servis-panel" : (from || "/"));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const chip = (active, accent) => `text-xs px-3 py-1.5 rounded-full border transition-colors ${
    active ? (accent === "orange" ? "border-orange-400/60 bg-orange-500/15 text-orange-300" : "border-cyan-400/60 bg-cyan-400/15 text-cyan-300")
           : "border-white/10 text-slate-400 hover:text-slate-200"}`;

  return (
    <div className="min-h-screen pt-24 pb-16 flex items-start justify-center px-4 carbon-texture">
      <div className={`w-full ${typeKey && kind !== "user" ? "max-w-2xl" : "max-w-md"} rounded-2xl backdrop-blur-xl bg-slate-900/80 border border-white/10 shadow-2xl p-6 sm:p-8 animate-fade-up`}>
        <div className="flex items-center gap-3">
          <span className="w-10 h-10 rounded-lg bg-cyan-400/10 border border-cyan-400/40 flex items-center justify-center">
            <ShieldCheck className="w-5 h-5 text-cyan-300" />
          </span>
          <h1 className="text-2xl font-bold text-white">Kayıt Ol</h1>
        </div>

        {!kind ? (
          <>
            <p data-testid="register-kind-title" className="mt-2 text-sm text-slate-400">Hesap Türünüzü Seçin</p>
            <div data-testid="register-kind-grid" className="mt-6 grid grid-cols-1 gap-3">
              <button data-testid="register-kind-user" onClick={() => { setKind("user"); setTypeKey("user"); }}
                className="flex flex-col items-start gap-2 p-5 rounded-xl border border-cyan-400/30 bg-cyan-400/5 text-left hover:border-cyan-400/60 hover:bg-cyan-400/10 transition-colors">
                <User className="w-6 h-6 text-cyan-300" />
                <span className="text-sm font-semibold text-slate-100">Bireysel Araç Sahibi</span>
                <span className="text-xs text-slate-400">Araçlarınızı kaydedin, geçmişini takip edin</span>
              </button>
              <button data-testid="register-kind-kurumsal" onClick={() => { setKind("kurumsal"); setTypeKey("user"); }}
                className="flex flex-col items-start gap-2 p-5 rounded-xl border border-white/10 text-left hover:border-cyan-400/50 hover:bg-slate-800/50 transition-colors">
                <Building2 className="w-6 h-6 text-orange-300" />
                <span className="text-sm font-semibold text-slate-100">Kurumsal Araç Sahibi</span>
                <span className="text-xs text-slate-400">Şirketinize / kurumunuza ait araçları kaydedin ve yönetin</span>
              </button>
              <button data-testid="register-kind-isletme" onClick={() => setKind("isletme")}
                className="flex flex-col items-start gap-2 p-5 rounded-xl border border-white/10 text-left hover:border-cyan-400/50 hover:bg-slate-800/50 transition-colors">
                <Building2 className="w-6 h-6 text-cyan-300" />
                <span className="text-sm font-semibold text-slate-100">İşletme</span>
                <span className="text-xs text-slate-400">Çekici, servis, galeri ve diğer işletmeler</span>
              </button>
            </div>
          </>
        ) : kind === "isletme" && !typeKey ? (
          <>
            <button type="button" data-testid="register-back-kind" onClick={() => setKind(null)}
              className="mt-4 flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-300 transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Hesap türünü değiştir
            </button>
            <p className="mt-4 text-sm text-slate-400">İşletme kayıt türünüzü seçin.</p>
            {catalog.length === 0 ? (
              <div className="flex justify-center py-8"><div className="w-7 h-7 border-2 border-cyan-400 border-t-transparent rounded-full animate-spin" /></div>
            ) : (
              <div data-testid="register-type-grid" className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {catalog.map((c) => {
                  const Icon = FAMILY_ICON[c.family] || Building2;
                  return (
                    <button key={c.key} data-testid={`register-type-${c.key}`} onClick={() => setTypeKey(c.key)}
                      className="flex items-center gap-3 p-4 rounded-xl border border-white/10 text-left hover:border-cyan-400/50 hover:bg-slate-800/50 transition-colors">
                      <Icon className={`w-5 h-5 shrink-0 ${c.family === "cekici" ? "text-orange-300" : "text-slate-400"}`} />
                      <span className="text-sm font-semibold text-slate-200">{c.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </>
        ) : (
          <form onSubmit={submit} className="mt-6 space-y-5">
            <button type="button" data-testid="register-back" onClick={() => { if (kind === "isletme") { setTypeKey(null); } else { setKind(null); setTypeKey(null); } setError(""); }}
              className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-cyan-300 transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> Kayıt türünü değiştir
            </button>
            {sel && (
              <p data-testid="register-selected-type" className="text-sm text-cyan-300 font-semibold">{sel.label}</p>
            )}

            {kind !== "isletme" ? (
              <p className="text-sm text-slate-400">{kind === "kurumsal" ? "Şirketinize ait araçları kaydedin, geçmişlerini dijital künyede saklayın." : "Araçlarınızı kaydedin, geçmişinizi dijital künyede saklayın."}</p>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className={labelCls}>Firma / İşletme Adı *</label>
                    <input data-testid="register-biz-company" required value={f.company_name} onChange={(e) => set("company_name", trUpper(e.target.value))} className={inputCls} /></div>
                  <div><label className={labelCls}>Vergi Numarası *</label>
                    <input data-testid="register-biz-tax" required value={f.tax_number} onChange={(e) => set("tax_number", e.target.value)} className={inputCls} /></div>
                  <div><label className={labelCls}>Yetkili Kişi Adı Soyadı *</label>
                    <input data-testid="register-name-input" required value={f.name} onChange={(e) => set("name", trUpper(e.target.value))} className={inputCls} /></div>
                  <div><label className={labelCls}>Telefon *</label>
                    <input data-testid="register-biz-phone" required value={f.phone} onChange={(e) => set("phone", e.target.value)} className={inputCls} placeholder="0 5xx xxx xx xx" /></div>
                  <div><label className={labelCls}>İl *</label>
                    <input data-testid="register-biz-city" required value={f.city} onChange={(e) => set("city", trUpper(e.target.value))} className={inputCls} placeholder="İstanbul" /></div>
                  <div><label className={labelCls}>İlçe *</label>
                    <input data-testid="register-biz-district" required value={f.district} onChange={(e) => set("district", trUpper(e.target.value))} className={inputCls} placeholder="Kadıköy" /></div>
                  <div className="sm:col-span-2">
                    <LocationButton testid="register-biz-geo-location" onLocated={(l) => { set("city", trUpper(l.city)); set("district", trUpper(l.district)); }} />
                  </div>
                  <div className="sm:col-span-2"><label className={labelCls}>Açık Adres *</label>
                    <input data-testid="register-biz-address" required value={f.address} onChange={(e) => set("address", trUpper(e.target.value))} className={inputCls} /></div>
                </div>

                <div>
                  <label className={labelCls}>Verilen Hizmetler *</label>
                  <div className="flex flex-wrap gap-2">
                    {(sel?.services || []).map((s) => (
                      <button key={s} type="button" data-testid={`register-biz-service-${slugify(s)}`}
                        onClick={() => toggleArr("services", s)} className={chip(f.services.includes(s), sel?.family === "cekici" ? "orange" : "cyan")}>
                        {s}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <label className={labelCls}>Hizmet Verilen Bölgeler *</label>
                  <div className="flex gap-2">
                    <input data-testid="register-biz-region-input" value={regionInput} onChange={(e) => setRegionInput(e.target.value)}
                      onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addRegion())}
                      className={inputCls} placeholder="İl veya ilçe yazın (örn. Kadıköy)" />
                    <button type="button" data-testid="register-biz-region-add" onClick={addRegion}
                      className="px-4 rounded-xl border border-cyan-400/40 text-cyan-300 text-sm font-semibold hover:bg-cyan-400/10 transition-colors whitespace-nowrap">Ekle</button>
                  </div>
                  {f.regions.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-2">
                      {f.regions.map((r) => (
                        <span key={r} data-testid={`register-biz-region-${slugify(r)}`}
                          className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-cyan-400/40 bg-cyan-400/10 text-cyan-300">
                          {r}
                          <button type="button" onClick={() => set("regions", f.regions.filter((x) => x !== r))}><X className="w-3 h-3" /></button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <label className={labelCls}>Çalışma Günleri</label>
                  <div className="flex flex-wrap gap-2">
                    {WORK_DAYS.map((d) => (
                      <button key={d} type="button" data-testid={`register-biz-day-${slugify(d)}`}
                        onClick={() => toggleArr("working_days", d)} className={chip(f.working_days.includes(d))}>
                        {d}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div><label className={labelCls}>Çalışma Saatleri</label>
                    <input data-testid="register-biz-hours" value={f.working_hours} onChange={(e) => set("working_hours", e.target.value)} className={inputCls} placeholder="08:30 - 18:30" /></div>
                  <div className="flex items-end gap-5 pb-1">
                    <label data-testid="register-biz-247" className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={f.is_247} onChange={(e) => set("is_247", e.target.checked)} className="accent-cyan-400 w-4 h-4" />
                      <span className="text-sm text-slate-300">7/24 hizmet</span>
                    </label>
                    <label data-testid="register-biz-available" className="flex items-center gap-2 cursor-pointer">
                      <input type="checkbox" checked={f.available} onChange={(e) => set("available", e.target.checked)} className="accent-emerald-400 w-4 h-4" />
                      <span className="text-sm text-slate-300">Müsait / Hizmet Veriyorum</span>
                    </label>
                  </div>
                  <div><label className={labelCls}>WhatsApp (opsiyonel)</label>
                    <input data-testid="register-biz-whatsapp" value={f.whatsapp} onChange={(e) => set("whatsapp", e.target.value)} className={inputCls} /></div>
                  <div><label className={labelCls}>Web Sitesi (opsiyonel)</label>
                    <input data-testid="register-biz-website" value={f.website} onChange={(e) => set("website", e.target.value)} className={inputCls} placeholder="https://" /></div>
                  <div className="sm:col-span-2"><label className={labelCls}>Tanıtım Yazısı (opsiyonel)</label>
                    <textarea data-testid="register-biz-description" rows={2} value={f.description} onChange={(e) => set("description", e.target.value)} className={inputCls} /></div>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                  {[["logo", "İşletme Logosu", true], ["photos", "İşletme Fotoğrafları", false], ["tax_document", "Vergi Levhası (opsiyonel)", true]].map(([key, label, single]) => (
                    <div key={key}>
                      <label data-testid={`register-biz-upload-${key}`}
                        className="flex flex-col items-center justify-center gap-1.5 px-3 py-4 rounded-xl border border-dashed border-white/20 text-slate-400 text-xs cursor-pointer hover:border-cyan-400/50 hover:text-cyan-300 transition-colors">
                        <Upload className="w-4 h-4" />
                        {uploading === key ? "Yükleniyor..." : label}
                        <input type="file" accept="image/*" multiple={!single} className="hidden"
                          onChange={async (e) => { await doUpload([...e.target.files], key, single); e.target.value = ""; }} />
                      </label>
                      {(single ? (f[key] ? [f[key]] : []) : f[key]).length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {(single ? [f[key]] : f[key]).map((p) => (
                            <div key={p} className="relative">
                              <img src={photoUrl(p)} alt="" className="w-14 h-10 object-cover rounded-lg border border-white/10" />
                              <button type="button" onClick={() => single ? set(key, "") : set(key, f[key].filter((x) => x !== p))}
                                className="absolute -top-1.5 -right-1.5 w-4 h-4 rounded-full bg-red-500 text-white flex items-center justify-center">
                                <X className="w-2.5 h-2.5" />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
                <p className="text-xs text-slate-500 flex items-start gap-1.5">
                  <BadgeCheck className="w-3.5 h-3.5 mt-0.5 shrink-0 text-emerald-400" />
                  Vergi levhası zorunlu değildir; yükleyen işletmeler admin onayı sonrası "Doğrulanmış İşletme" rozeti alır. Belgeniz yalnızca yönetici tarafından görülür.
                </p>

                <button type="button" data-testid="register-biz-location"
                  onClick={() => {
                    if (!navigator.geolocation) { setError("Tarayıcınız konum desteklemiyor."); return; }
                    navigator.geolocation.getCurrentPosition(
                      (p) => setF((pr) => ({ ...pr, lat: p.coords.latitude, lng: p.coords.longitude })),
                      () => setError("Konum izni verilmedi, adres bilgileriniz yeterlidir."), { timeout: 10000 });
                  }}
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl border border-cyan-400/40 text-cyan-300 text-sm font-semibold hover:bg-cyan-400/10 transition-colors">
                  <Navigation className="w-4 h-4" /> {f.lat ? "Harita Konumu Alındı ✓" : "Harita Konumumu Al"}
                </button>
              </>
            )}

            {kind !== "isletme" && (
              <>
                {kind === "kurumsal" && (
                  <div>
                    <label className={labelCls}>Firma / Kurum Adı (opsiyonel)</label>
                    <input data-testid="register-kurumsal-company" type="text" value={f.company_name}
                      onChange={(e) => set("company_name", trUpper(e.target.value))} className={inputCls} placeholder="Firma adı" />
                  </div>
                )}
                <div>
                  <label className={labelCls}>Ad Soyad</label>
                  <input data-testid="register-name-input" type="text" required value={f.name}
                    onChange={(e) => set("name", trUpper(e.target.value))} className={inputCls} placeholder="Adınız Soyadınız" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className={labelCls}>İl (opsiyonel)</label>
                    <input data-testid="register-user-city" value={f.city}
                      onChange={(e) => set("city", trUpper(e.target.value))} className={inputCls} placeholder="Kocaeli" />
                  </div>
                  <div>
                    <label className={labelCls}>İlçe (opsiyonel)</label>
                    <input data-testid="register-user-district" value={f.district}
                      onChange={(e) => set("district", trUpper(e.target.value))} className={inputCls} placeholder="Çayırova" />
                  </div>
                </div>
                <LocationButton testid="register-user-location" className="w-full"
                  onLocated={(l) => { set("city", trUpper(l.city)); set("district", trUpper(l.district)); }} />
                <p className="text-[11px] text-slate-500">İl/ilçe bilginiz teklif taleplerinde "Kayıtlı Konumum" olarak otomatik kullanılır; talep bazında farklı konum seçebilirsiniz.</p>
              </>
            )}
            <div>
              <label className={labelCls}>E-posta</label>
              <input data-testid="register-email-input" type="email" required value={f.email}
                onChange={(e) => set("email", e.target.value)} className={inputCls} placeholder="ornek@eposta.com" />
            </div>
            <div>
              <label className={labelCls}>Şifre</label>
              <input data-testid="register-password-input" type="password" required minLength={6} value={f.password}
                onChange={(e) => set("password", e.target.value)} className={inputCls} placeholder="En az 6 karakter" />
            </div>
            {kind !== "isletme" && (
              <label data-testid="register-consent-label" className="flex items-start gap-2.5 cursor-pointer">
                <input data-testid="register-consent-checkbox" type="checkbox" checked={!!f.data_consent}
                  onChange={(e) => set("data_consent", e.target.checked)} className="accent-cyan-400 w-4 h-4 mt-0.5 shrink-0" />
                <span className="text-xs text-slate-400 leading-relaxed">
                  Araç bilgilerimin araç sorgulamalarında görüntülenmesini ve teklif süreçlerinde işletmelerle paylaşılmasını onaylıyorum. Bu onay bir kez alınır ve tekrar sorulmaz; belgelerim (ruhsat, sigorta vb.) bu kapsamın dışındadır, ben açıkça paylaşmadıkça kimseye gösterilmez.
                </span>
              </label>
            )}
            {error && <p data-testid="register-error" className="text-sm text-red-400">{error}</p>}
            <button data-testid="register-form-submit-button" type="submit" disabled={loading || !!uploading}
              className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl bg-cyan-400 text-slate-950 font-bold hover:bg-cyan-300 hover:shadow-[0_0_20px_rgba(249,115,22,0.4)] active:scale-[0.99] transition-all disabled:opacity-50">
              <UserPlus className="w-4 h-4" /> {loading ? "Hesap oluşturuluyor..." : kind === "isletme" ? "İşletme Başvurusunu Gönder" : "Hesap Oluştur"}
            </button>
            {kind === "isletme" && (
              <p className="text-xs text-slate-500 text-center">Başvurunuz yönetici onayından sonra aktifleşir.</p>
            )}
          </form>
        )}

        <p className="mt-6 text-sm text-slate-400 text-center">
          Zaten hesabınız var mı?{" "}
          <Link data-testid="go-login-link" to="/giris" state={{ from }} className="text-cyan-300 hover:text-cyan-200 font-semibold">
            Giriş Yapın
          </Link>
        </p>
        <p className="mt-4 text-xs text-slate-500 text-center">
          Kayıt olarak{" "}
          <a data-testid="register-privacy-link" href="/gizlilik-politikasi" target="_blank" rel="noopener noreferrer"
            className="hover:text-cyan-300 transition-colors underline underline-offset-2">
            Otokünye Gizlilik Politikası
          </a>'nı kabul etmiş olursunuz.
        </p>
      </div>
    </div>
  );
}
